import React, {useState} from "react";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {digits, tagsOf} from "./help-cms";
import {Card, Note, TabHeader} from "./ui";

const newCategorySlug = () => `cat-${Date.now().toString(36)}`;

// Add / rename dialog shared by categories and tags.
function NameDialog({state, onHide, onSubmit}) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [icon, setIcon] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(false);

    React.useEffect(() => {
        if (!state) return;
        setTitle(state.item?.title || "");
        setDescription(state.item?.description || "");
        setIcon(state.item?.icon || "");
        setError(false);
    }, [state]);

    const submit = async () => {
        if (!title.trim()) return;
        setSaving(true);
        setError(false);
        try {
            await onSubmit({title: title.trim(), description: description.trim(), icon: icon.trim()});
            onHide();
        } catch (e) {
            setError(true);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog visible={Boolean(state)} onHide={onHide} className="rtl w-11 md:w-4" header={state?.header}
                footer={<div className="flex flex-row-reverse gap-2">
                    <Button className="text-xs" label="ذخیره" loading={saving} onClick={submit}/>
                    <Button className="p-button-text text-xs" label="انصراف" onClick={onHide}/>
                </div>}>
            <div className="flex flex-column gap-3">
                <InputText value={title} onChange={(e) => setTitle(e.target.value)} placeholder="عنوان"/>
                {state?.kind === "category" && (
                    <>
                        <InputText value={description} onChange={(e) => setDescription(e.target.value)} placeholder="توضیح کوتاه دسته"/>
                        <InputText className="ltr" value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="folder_open (نام آیکون)"/>
                    </>
                )}
                {error && <small className="p-error">ذخیره انجام نشد؛ شاید این نام تکراری است.</small>}
            </div>
        </Dialog>
    );
}

export default function TaxonomyTab({data, reload, openTab}) {
    const {contents, categories, tags, api} = data;
    const [dialog, setDialog] = useState(null);

    const countIn = (slug) => contents.filter((item) => item.categorySlug === slug).length;
    const tagCount = (title) => contents.filter((item) => tagsOf(item).includes(title)).length;

    const move = async (index, direction) => {
        const other = categories[index + direction];
        if (!other) return;
        const current = categories[index];
        // sortOrder may be equal on old rows, so write explicit positions for both
        await api.updateCategory(current, {sortOrder: index + direction});
        await api.updateCategory(other, {sortOrder: index});
        reload();
    };

    const submit = async (values) => {
        if (dialog.kind === "category") {
            if (dialog.item) await api.updateCategory(dialog.item, values);
            else await api.createCategory({...values, slug: newCategorySlug(), icon: values.icon || "folder_open", sortOrder: categories.length});
        } else if (dialog.item) {
            await api.updateTag(dialog.item, {title: values.title});
        } else {
            await api.createTag({title: values.title, isPopular: false, sortOrder: tags.length});
        }
        reload();
    };

    const removeTag = async (tag) => {
        if (!window.confirm(`برچسب «${tag.title}» حذف شود؟`)) return;
        await api.deleteTag(tag);
        reload();
    };

    return (
        <div className="flex flex-column gap-3">
            <TabHeader kicker="ساختار محتوا" title="دسته‌ها و برچسب‌ها" description="دسته مسیر اصلی مرور است؛ برچسب برای ارتباط و جست‌وجوی دقیق‌تر."/>
            <div className="grid m-0">
                <div className="col-12 lg:col-7">
                    <Card title="دسته‌بندی‌ها" description="ترتیب این فهرست در سایدبار عمومی نمایش داده می‌شود."
                          action={<Button className="p-button-outlined p-button-sm text-xs" label="+ دسته" onClick={() => setDialog({kind: "category", header: "دسته جدید"})}/>}>
                        <div className="flex flex-column gap-2">
                            {categories.map((category, index) => (
                                <div key={category.documentId} className="flex align-items-center gap-3 border-1 border-200 border-round-lg p-2">
                                    <span className="text-400">⠿</span>
                                    <span className="surface-100 border-round-lg flex align-items-center justify-content-center" style={{width: 40, height: 40}}>
                                        <i className="material-icons-outlined text-primary" style={{fontSize: 22}}>{category.icon || "folder_open"}</i>
                                    </span>
                                    <div className="flex-1">
                                        <b className="block text-sm">{category.title}</b>
                                        <small className="text-500">{digits(countIn(category.slug))} محتوای متصل</small>
                                    </div>
                                    <div className="flex gap-1">
                                        <Button className="p-button-text p-button-sm" icon="pi pi-arrow-up" aria-label="بالا" disabled={index === 0} onClick={() => move(index, -1)}/>
                                        <Button className="p-button-text p-button-sm" icon="pi pi-arrow-down" aria-label="پایین" disabled={index === categories.length - 1} onClick={() => move(index, 1)}/>
                                        <Button className="p-button-outlined p-button-sm text-xs" label="تغییر نام" onClick={() => setDialog({kind: "category", item: category, header: "ویرایش دسته"})}/>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
                <div className="col-12 lg:col-5">
                    <Card title="برچسب‌ها" description="برای فیلتر، مترادف‌ها و پیدا شدن بهتر."
                          action={<Button className="p-button-outlined p-button-sm text-xs" label="+ برچسب" onClick={() => setDialog({kind: "tag", header: "برچسب جدید"})}/>}>
                        <div className="grid m-0">
                            {tags.map((tag) => (
                                <div key={tag.documentId} className="col-6 p-1">
                                    <div className="border-1 border-200 border-round-lg p-2 flex align-items-center gap-1 hover:border-primary">
                                        <button type="button" className="flex-1 text-right bg-transparent border-none cursor-pointer p-0" onClick={() => openTab("contents", {tag: tag.title})}>
                                            <b className="block text-sm">#{tag.title}</b>
                                            <small className="text-500">{digits(tagCount(tag.title))} محتوا</small>
                                        </button>
                                        <Button className="p-button-text p-button-sm" icon="pi pi-pencil" aria-label="تغییر نام" onClick={() => setDialog({kind: "tag", item: tag, header: "ویرایش برچسب"})}/>
                                        <Button className="p-button-text p-button-danger p-button-sm" icon="pi pi-trash" aria-label="حذف" onClick={() => removeTag(tag)}/>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Note>برچسب جای دسته‌بندی را نمی‌گیرد. هر محتوا یک دسته اصلی و چند برچسب دارد.</Note>
                    </Card>
                </div>
            </div>
            <NameDialog state={dialog} onHide={() => setDialog(null)} onSubmit={submit}/>
        </div>
    );
}
